import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useGetSupervisorLogbooksGroupedByDietQuery } from "../api/logbookApi";
import {
  ChevronLeft,
  RefreshCw,
  Calendar,
  User,
  ArrowRight,
  ChevronDown,
  CheckCircle2,
  Clock,
  Search,
  Filter,
} from "lucide-react";

function normalizeGrouped(res: any): any[] {
  const arr = res?.data ?? res?.data?.data ?? res?.data?.data?.data;
  if (Array.isArray(arr)) return arr;
  if (Array.isArray(res)) return res;
  return [];
}

function personNameFromApplication(app: any) {
  if (!app) return "—";
  return [app?.title, app?.surname, app?.other_names || app?.othernames]
    .filter(Boolean)
    .join(" ");
}

// --- DYNAMIC PROGRESS COLORING ---
const getProgressTheme = (percent: number) => {
  if (percent <= 25)
    return {
      bar: "bg-rose-500",
      text: "text-rose-600",
      bg: "bg-rose-50",
      shadow: "shadow-rose-200",
    };
  if (percent <= 50)
    return {
      bar: "bg-orange-500",
      text: "text-orange-600",
      bg: "bg-orange-50",
      shadow: "shadow-orange-200",
    };
  if (percent <= 75)
    return {
      bar: "bg-blue-500",
      text: "text-blue-600",
      bg: "bg-blue-50",
      shadow: "shadow-blue-200",
    };
  return {
    bar: "bg-emerald-500",
    text: "text-emerald-600",
    bg: "bg-emerald-50",
    shadow: "shadow-emerald-200",
  };
};

const StatusBadge = ({ status }: { status?: string | null }) => {
  const normalized = ["accepted", null, undefined].includes(status as any) ? "in_progress" : status!;
  const styles: Record<string, string> = {
    in_progress: "bg-amber-100/50 text-amber-700 border-amber-200",
    submitted:   "bg-indigo-100/50 text-indigo-700 border-indigo-200",
    graded:      "bg-emerald-100/50 text-emerald-700 border-emerald-200",
    repeating:   "bg-orange-100/50 text-orange-700 border-orange-200",
    completed:   "bg-teal-100/50 text-teal-700 border-teal-200",
  };
  return (
    <span className={`text-[10px] tracking-tighter font-black px-2.5 py-1 rounded-md border ${styles[normalized] ?? "bg-gray-100 text-gray-600 border-gray-200"}`}>
      {normalized.replace(/_/g, " ").toUpperCase()}
    </span>
  );
};

export default function SupervisorDashboard() {
  const navigate = useNavigate();
  const {
    data: raw,
    isLoading,
    isFetching,
    refetch,
  } = useGetSupervisorLogbooksGroupedByDietQuery();

  const busy = isLoading || isFetching;
  const grouped = useMemo(() => normalizeGrouped(raw), [raw]);
  const [expandedDiets, setExpandedDiets] = useState<Record<string, boolean>>({});
  const [dietSearches, setDietSearches]   = useState<Record<string, string>>({});
  const [dietFilters, setDietFilters]     = useState<Record<string, string>>({});

  const toggleDiet = (dietTitle: string) => {
    setExpandedDiets((prev) => ({ ...prev, [dietTitle]: !prev[dietTitle] }));
  };

  return (
    <div className="min-h-screen bg-[#FDFDFD] pb-32">
      <div className="mx-auto max-w-[1500px] px-6 lg:px-10 py-12">
        {/* HEADER AREA */}
        <header className="flex flex-col md:flex-row md:items-center justify-between gap-8 mb-20">
          <div>
            <h1 className="text-6xl font-[1000] text-slate-900 tracking-[-0.04em] mb-4">
              Overview<span className="text-primary italic">.</span>
            </h1>
            <p className="text-slate-400 text-lg font-medium">
              Monitoring {grouped.length} active Diet cycles for NIQS.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-3.5 rounded-2xl text-sm font-bold text-slate-500 bg-slate-100 hover:bg-slate-200 transition-all flex items-center gap-2"
            >
              <ChevronLeft size={18} /> Back
            </button>
            <button
              onClick={() => refetch()}
              className="px-8 py-3.5 bg-primary text-white rounded-2xl text-sm font-bold hover:scale-[1.02] active:scale-95 transition-all shadow-xl shadow-primary/20 flex items-center gap-2"
              disabled={busy}
            >
              <RefreshCw size={18} className={busy ? "animate-spin" : ""} />
              {busy ? "Syncing..." : "Refresh Data"}
            </button>
          </div>
        </header>

        {/* DIET LIST */}
        <div className="space-y-16">
          {grouped.map((g: any) => {
            const dietTitle = g?.diet_title || g?.diet?.title || "Unknown Diet";
            const items: any[] = Array.isArray(g?.logbooks) ? g.logbooks : [];
            const isExpanded = !!expandedDiets[dietTitle];

            // Calculate stats for the diet summary
            const graded     = items.filter((i) => i.status === "graded").length;
            const submitted  = items.filter((i) => i.status === "submitted").length;
            const inProgress = items.filter((i) => ["in_progress", "accepted", null, undefined].includes(i.status)).length;

            return (
              <div key={dietTitle} className="relative">
                {/* DIET HEADER CARD */}
                <div
                  onClick={() => toggleDiet(dietTitle)}
                  className={`group cursor-pointer flex flex-col lg:flex-row lg:items-center justify-between p-8 rounded-[2.5rem] transition-all duration-500 border-2 ${
                    isExpanded
                      ? "bg-white border-slate-900 shadow-2xl"
                      : "bg-slate-50 border-transparent hover:border-slate-200"
                  }`}
                >
                  <div className="flex items-center gap-6">
                    <div
                      className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-colors ${
                        isExpanded
                          ? "bg-primary text-white"
                          : "bg-white text-slate-400 shadow-sm"
                      }`}
                    >
                      <Calendar size={24} strokeWidth={2.5} />
                    </div>
                    <div>
                      <h2
                        className={`text-3xl font-black tracking-tight ${
                          isExpanded ? "text-slate-900" : "text-slate-600"
                        }`}
                      >
                        {dietTitle}
                      </h2>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        <span className="flex items-center gap-1.5 text-xs font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-md">
                          <Clock size={12} /> {inProgress} In Progress
                        </span>
                        <span className="flex items-center gap-1.5 text-xs font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-md">
                          <Clock size={12} /> {submitted} Submitted
                        </span>
                        <span className="flex items-center gap-1.5 text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md">
                          <CheckCircle2 size={12} /> {graded} Graded
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 lg:mt-0 flex items-center gap-4">
                    <p className="text-right hidden lg:block">
                      <span className="block text-sm font-black text-slate-900">
                        {items.length} Candidates
                      </span>
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                        Active Enrollment
                      </span>
                    </p>
                    <div
                      className={`p-3 rounded-full border-2 transition-all ${
                        isExpanded
                          ? "border-slate-900 rotate-180"
                          : "border-slate-200 rotate-0"
                      }`}
                    >
                      <ChevronDown size={20} strokeWidth={3} />
                    </div>
                  </div>
                </div>

                {/* LOGBOOK TABLE INSIDE DIET */}
                {isExpanded && (() => {
                  const search = (dietSearches[dietTitle] ?? "").toLowerCase();
                  const statusFilter = dietFilters[dietTitle] ?? "all";

                  const filtered = items.filter((lb: any) => {
                    const app = lb?.application;
                    const name  = personNameFromApplication(app).toLowerCase();
                    const email = (app?.email ?? lb?.user?.email ?? "").toLowerCase();
                    const phone = (app?.phone ?? "").toLowerCase();
                    const matchSearch = !search || name.includes(search) || email.includes(search) || phone.includes(search);
                    const lbStatus = ["in_progress", "accepted", null, undefined].includes(lb.status)
                      ? "in_progress"
                      : (lb.status ?? "in_progress");
                    const matchStatus = statusFilter === "all" || lbStatus === statusFilter;
                    return matchSearch && matchStatus;
                  });

                  const STATUS_OPTIONS = [
                    { value: "all",         label: "All" },
                    { value: "in_progress", label: "In Progress" },
                    { value: "submitted",   label: "Submitted" },
                    { value: "graded",      label: "Graded" },
                    { value: "repeating",   label: "Repeating" },
                    { value: "completed",   label: "Completed" },
                  ];

                  return (
                  <div className="mt-4 bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                    {/* Search & Filter bar */}
                    <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center px-4 py-3 border-b border-slate-100 bg-slate-50">
                      <div className="relative flex-1 w-full sm:max-w-xs">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                          type="text"
                          placeholder="Search by name, email or phone…"
                          value={dietSearches[dietTitle] ?? ""}
                          onChange={(e) =>
                            setDietSearches((prev) => ({ ...prev, [dietTitle]: e.target.value }))
                          }
                          className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-slate-200 bg-white"
                        />
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Filter size={14} className="text-slate-400" />
                        <div className="flex gap-1">
                          {STATUS_OPTIONS.map((opt) => (
                            <button
                              key={opt.value}
                              onClick={() =>
                                setDietFilters((prev) => ({ ...prev, [dietTitle]: opt.value }))
                              }
                              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                statusFilter === opt.value
                                  ? "bg-slate-900 text-white"
                                  : "bg-white border border-slate-200 text-slate-500 hover:border-slate-400"
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="hidden md:block overflow-x-auto">
                      <table className="w-full text-sm text-left">
                        <thead>
                          <tr className="bg-slate-50 border-b border-slate-200 text-[11px] font-black text-slate-400 uppercase tracking-widest">
                            <th className="py-4 px-4 w-10">#</th>
                            <th className="py-4 px-4">Candidate</th>
                            <th className="py-4 px-4">Email</th>
                            <th className="py-4 px-4">Status</th>
                            <th className="py-4 px-4">Progress</th>
                            <th className="py-4 px-4">Weeks</th>
                            <th className="py-4 px-4 text-right">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filtered.length === 0 ? (
                            <tr>
                              <td colSpan={7} className="py-10 text-center text-slate-400 font-medium">
                                {items.length === 0 ? "No candidates in this diet." : "No results match your search or filter."}
                              </td>
                            </tr>
                          ) : (
                            filtered.map((lb: any, idx: number) => {
                              const progress = Math.min(
                                Math.round(((lb.position ?? 0) / (lb.size || 1)) * 100),
                                100
                              );
                              const theme = getProgressTheme(progress);
                              const app = lb?.application;
                              const name = personNameFromApplication(app);
                              const email = app?.email ?? lb?.user?.email ?? "—";
                              const phone = app?.phone ?? "—";
                              const membershipNo = lb?.user?.member?.membership_no ?? null;

                              return (
                                <tr
                                  key={lb.id}
                                  className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                                >
                                  {/* # */}
                                  <td className="py-4 px-4 text-slate-400 font-mono text-xs">
                                    {String(idx + 1).padStart(2, "0")}
                                  </td>

                                  {/* Candidate */}
                                  <td className="py-4 px-4">
                                    <div className="flex items-center gap-3">
                                      <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                                        <User size={16} className="text-slate-400" />
                                      </div>
                                      <div>
                                        <div className="font-bold text-slate-900 uppercase tracking-tight text-sm">
                                          {name}
                                        </div>
                                        <div className="text-[10px] text-slate-400 font-mono mt-0.5">{phone}</div>
                                        {membershipNo && (
                                          <div className="text-[10px] font-bold text-indigo-600 font-mono mt-0.5">
                                            {membershipNo}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </td>

                                  {/* Email */}
                                  <td className="py-4 px-4 text-slate-500 text-xs">{email}</td>

                                  {/* Status */}
                                  <td className="py-4 px-4">
                                    <StatusBadge status={lb.status} />
                                  </td>

                                  {/* Progress bar */}
                                  <td className="py-4 px-4 min-w-[140px]">
                                    <div className="flex items-center gap-2">
                                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                                        <div
                                          className={`h-full rounded-full ${theme.bar}`}
                                          style={{ width: `${progress}%` }}
                                        />
                                      </div>
                                      <span className={`text-xs font-black w-9 text-right ${theme.text}`}>
                                        {progress}%
                                      </span>
                                    </div>
                                  </td>

                                  {/* Weeks */}
                                  <td className="py-4 px-4 text-xs text-slate-500 font-medium whitespace-nowrap">
                                    Wk {lb.position ?? 0} / {lb.size ?? "—"}
                                  </td>

                                  {/* Action */}
                                  <td className="py-4 px-4 text-right">
                                    <Link
                                      to={`/supervisor/logbooks/${lb.id}`}
                                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-900 text-white rounded-xl text-xs font-black hover:bg-primary transition-all"
                                    >
                                      Open <ArrowRight size={12} />
                                    </Link>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile: cards */}
                    <div className="md:hidden divide-y divide-slate-100">
                      {filtered.length === 0 ? (
                        <div className="py-10 text-center text-slate-400 font-medium text-sm">
                          {items.length === 0 ? "No candidates in this diet." : "No results match your search or filter."}
                        </div>
                      ) : (
                        filtered.map((lb: any, idx: number) => {
                          const progress = Math.min(
                            Math.round(((lb.position ?? 0) / (lb.size || 1)) * 100),
                            100
                          );
                          const theme = getProgressTheme(progress);
                          const app = lb?.application;
                          const name = personNameFromApplication(app);
                          const email = app?.email ?? lb?.user?.email ?? "—";
                          const phone = app?.phone ?? "—";
                          const membershipNo = lb?.user?.member?.membership_no ?? null;

                          return (
                            <div key={lb.id} className="p-4 space-y-3">
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex items-center gap-3 min-w-0">
                                  <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                                    <User size={16} className="text-slate-400" />
                                  </div>
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="text-slate-300 font-mono text-[10px]">{String(idx + 1).padStart(2, "0")}</span>
                                      <span className="font-bold text-slate-900 uppercase tracking-tight text-sm truncate">{name}</span>
                                    </div>
                                    <div className="text-[11px] text-slate-500 break-all mt-0.5">{email}</div>
                                    <div className="text-[10px] text-slate-400 font-mono mt-0.5">{phone}</div>
                                    {membershipNo && (
                                      <div className="text-[10px] font-bold text-indigo-600 font-mono mt-0.5">{membershipNo}</div>
                                    )}
                                  </div>
                                </div>
                                <StatusBadge status={lb.status} />
                              </div>

                              <div className="flex items-center gap-2">
                                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                                  <div className={`h-full rounded-full ${theme.bar}`} style={{ width: `${progress}%` }} />
                                </div>
                                <span className={`text-xs font-black w-9 text-right ${theme.text}`}>{progress}%</span>
                              </div>

                              <div className="flex items-center justify-between">
                                <span className="text-xs text-slate-500 font-medium">
                                  Wk {lb.position ?? 0} / {lb.size ?? "—"}
                                </span>
                                <Link
                                  to={`/supervisor/logbooks/${lb.id}`}
                                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-900 text-white rounded-xl text-xs font-black hover:bg-primary transition-all"
                                >
                                  Open <ArrowRight size={12} />
                                </Link>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>

                    <div className="px-4 py-3 bg-slate-50 border-t border-slate-100 text-[11px] text-slate-400 font-semibold">
                      {filtered.length} of {items.length} candidate{items.length !== 1 ? "s" : ""}
                      {(search || statusFilter !== "all") && (
                        <button
                          onClick={() => {
                            setDietSearches((prev) => ({ ...prev, [dietTitle]: "" }));
                            setDietFilters((prev) => ({ ...prev, [dietTitle]: "all" }));
                          }}
                          className="ml-3 text-primary font-bold hover:underline"
                        >
                          Clear filters
                        </button>
                      )}
                    </div>
                  </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
